import { archiveTitle, ArchiveGroups, ArchiveMetadata, ArchiveTitleButton } from "@/components/dokumenti/ArchivePresentation";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { errorMessage } from "@/lib/api";
import { getCompanyDocuments, type CompanyDocument } from "@/lib/companyDocuments";
import { datum } from "@/lib/format";
import { CompanyDocumentDelete } from "./CompanyDocumentDelete";
import { CompanyDocumentPreview } from "./CompanyDocumentPreview";

export function CompanyDocumentArchive({ gradilisteId = null, onChanged, onClose }: {
  gradilisteId?: string | null;
  onChanged?: () => void;
  onClose?: () => void;
}) {
  const { can, user } = useAuth();
  const canRead = user?.permissions.some((permission) => permission.module === "company_document" && permission.action === "read" && permission.scope === "all") ?? false;
  const canDelete = canRead && can("dokument", "confirm_delete");
  const [search, setSearch] = useState("");
  const [showDeleted, setShowDeleted] = useState(false);
  const [documents, setDocuments] = useState<CompanyDocument[] | null>(null);
  const [revision, setRevision] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<CompanyDocument | null>(null);
  const includeDeleted = canDelete && showDeleted;
  useEffect(() => {
    if (!canRead) return;
    const controller = new AbortController();
    void getCompanyDocuments({ search, ...(gradilisteId ? { gradiliste_id: gradilisteId } : {}), ...(includeDeleted ? { include_deleted: true } : {}) }, controller.signal)
      .then((rows) => { if (!controller.signal.aborted) { setDocuments(rows); setError(null); } })
      .catch((cause: unknown) => { if (!controller.signal.aborted) setError(errorMessage(cause, "Arhiva se ne može učitati.")); });
    return () => controller.abort();
  }, [canRead, gradilisteId, search, includeDeleted, revision]);
  if (!canRead) return <p>Nemate ovlaštenje za arhivu tvrtke i gradilišta.</p>;
  if (preview) return <CompanyDocumentPreview key={preview.id} document={preview} onUpdated={updated => { setPreview(updated); setDocuments(current => current?.map(row => row.id === updated.id ? updated : row) ?? null); }} onBack={() => setPreview(null)} />;
  const visible = documents?.filter((document) => document.gradiliste_id === gradilisteId && (includeDeleted || !document.deleted_at));
  return <section className="flex h-full min-h-0 flex-1 flex-col gap-3" aria-label={gradilisteId ? "Arhiva gradilišta" : "Arhiva tvrtke"}>
    <div className="flex shrink-0 flex-wrap items-center gap-2">
      <h3 className="mr-auto font-semibold">{gradilisteId ? "Arhiva gradilišta" : "Arhiva tvrtke"}</h3>
      {canDelete && <Button type="button" variant="outline" aria-pressed={includeDeleted} onClick={() => setShowDeleted((current) => !current)}>{includeDeleted ? "Sakrij obrisane" : "Prikaži obrisane"}</Button>}
      {onClose && <Button type="button" variant="outline" onClick={onClose}>Zatvori arhivu</Button>}
    </div>
    <label>Pretraži arhivu<Input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Naziv dokumenta" /></label>
    {error && <p role="alert">{error}</p>}
    {!documents && !error && <p>Učitavanje arhive…</p>}
    {visible?.length === 0 && <p>Nema dokumenata za odabrani prikaz.</p>}
    <div className="min-h-0 flex-1 space-y-3 overflow-y-auto">
      <ArchiveGroups documents={visible ?? []}>{rows => rows.map((document) => <article key={document.id} className="avm-surface rounded border p-3">
        {document.deleted_at ? <div><p className="font-semibold">{archiveTitle(document)}</p><p className="text-sm">Obrisano {datum(document.deleted_at)} · {document.deleted_by_name ?? "Korisnik nije naveden"}</p></div> : <div className="flex flex-wrap items-start justify-between gap-2"><ArchiveTitleButton onClick={() => setPreview(document)}>{archiveTitle(document)}</ArchiveTitleButton><CompanyDocumentDelete document={{ ...document, naziv: archiveTitle(document) }} onDeleted={(deleted) => {
          setDocuments((current) => current?.map((row) => row.id === deleted.id ? deleted : row) ?? null);
          setRevision((current) => current + 1); onChanged?.();
        }} /></div>}
        <ArchiveMetadata document={document} verified={document.verified} deleted={!!document.deleted_at} />
        {document.opis && <p className="whitespace-pre-wrap text-sm">{document.opis}</p>}
        {!document.deleted_at && document.retention_review_required && <p className="text-sm">Pravilo čuvanja treba provjeriti</p>}
      </article>)}</ArchiveGroups>
    </div>
  </section>;
}
